import { useEffect, useMemo, useState } from 'react'
import dayjs from 'dayjs'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material'
import {
  AccessTime,
  Add,
  Close,
  ContentCopy,
  DeleteOutline,
  EditCalendar,
  EventBusy,
  PhoneInTalk,
  TipsAndUpdates,
  VideoCameraFront,
} from '@mui/icons-material'
import { alpha, useTheme } from '@mui/material/styles'
import { toast } from 'react-toastify'
import { EVENT_TYPES_ARRAY, getEventTypeObject, timezones_list } from 'src/consumer/constants'
import {
  getDataMapFromCommonResponse,
  getMessageMapFromCommonResponse,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'
import {
  createRosterSchedule,
  updateRosterSchedule,
} from 'src/supportPortal/services/rosterManagementService'

const DEFAULT_TIMEZONE = 'Asia/Kolkata'
const DEFAULT_SLOT = { start: '09:00', end: '17:00' }
const WEEKDAY_KEYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday']
const WEEKEND_KEYS = ['saturday', 'sunday']
const ROSTERING_MODE_CHAMBER_PRESENCE = 'chamber-presence'
const ROSTERING_MODE_ON_CALL = 'on-call'
const DAYS_OF_WEEK = [
  { key: 'monday', label: 'Monday' },
  { key: 'tuesday', label: 'Tuesday' },
  { key: 'wednesday', label: 'Wednesday' },
  { key: 'thursday', label: 'Thursday' },
  { key: 'friday', label: 'Friday' },
  { key: 'saturday', label: 'Saturday' },
  { key: 'sunday', label: 'Sunday' },
]

function createEmptyWeeklyHourMap() {
  return DAYS_OF_WEEK.reduce((acc, day) => {
    acc[day.key] = []
    return acc
  }, {})
}

function sanitizeStringList(values = []) {
  if (!Array.isArray(values)) {
    return []
  }

  return Array.from(
    new Set(
      values
        .map((value) => (typeof value === 'string' ? value.trim() : ''))
        .filter(Boolean),
    ),
  )
}

function toReadableLabel(value) {
  if (!value) {
    return ''
  }

  return value
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (match) => match.toUpperCase())
}

function getEventTypeLabel(eventTypeId) {
  if (!eventTypeId) {
    return ''
  }

  const directMatch = getEventTypeObject(eventTypeId)
  if (directMatch?.title) {
    return directMatch.title
  }

  const normalizedMatch = getEventTypeObject(eventTypeId.replace(/_/g, '-'))
  if (normalizedMatch?.title) {
    return normalizedMatch.title
  }

  return toReadableLabel(eventTypeId)
}

function hasEventTypeRosteringDetails(room) {
  return Array.isArray(room?.eventTypeRosteringDetails)
    && room.eventTypeRosteringDetails.some((detail) => detail?.eventType)
}

function getRoomRosterEventTypes(room) {
  const primaryEventTypes = sanitizeStringList(room?.primaryEventTypesForThisRoom)
  if (primaryEventTypes.length > 0) {
    return primaryEventTypes
  }
  return sanitizeStringList(room?.eventTypesForThisRoom)
}

function getRosteringModeLabel(mode) {
  if (mode === ROSTERING_MODE_ON_CALL) {
    return 'On-call contact'
  }
  return 'Check-in presence'
}

function buildEventTypeRosteringRows(room) {
  const roomEventTypes = getRoomRosterEventTypes(room)
  if (!hasEventTypeRosteringDetails(room)) {
    return []
  }

  return room.eventTypeRosteringDetails
    .filter((detail) => detail?.eventType && (roomEventTypes.length === 0 || roomEventTypes.includes(detail.eventType)))
    .map((detail) => ({
      eventType: detail.eventType,
      rostered: detail.rostered !== false,
      rosteringMode: detail.rosteringMode || ROSTERING_MODE_CHAMBER_PRESENCE,
    }))
}

function getMinutesFromTime(value) {
  if (!value || !/^\d{2}:\d{2}$/.test(value)) {
    return null
  }

  const [hours, minutes] = value.split(':').map(Number)

  if (
    Number.isNaN(hours) ||
    Number.isNaN(minutes) ||
    hours < 0 ||
    hours > 23 ||
    minutes < 0 ||
    minutes > 59
  ) {
    return null
  }

  return hours * 60 + minutes
}

function getTimeFromMinutes(totalMinutes) {
  if (typeof totalMinutes !== 'number' || Number.isNaN(totalMinutes)) {
    return DEFAULT_SLOT.end
  }

  const safeMinutes = Math.max(0, Math.min(totalMinutes, 23 * 60 + 59))
  const hours = Math.floor(safeMinutes / 60)
  const minutes = safeMinutes % 60

  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
}

function compareTimeRanges(a, b) {
  const startA = getMinutesFromTime(a?.start) ?? Number.MAX_SAFE_INTEGER
  const startB = getMinutesFromTime(b?.start) ?? Number.MAX_SAFE_INTEGER

  if (startA !== startB) {
    return startA - startB
  }

  return (getMinutesFromTime(a?.end) ?? Number.MAX_SAFE_INTEGER)
    - (getMinutesFromTime(b?.end) ?? Number.MAX_SAFE_INTEGER)
}

function sanitizeSlots(slots = []) {
  if (!Array.isArray(slots)) {
    return []
  }

  return slots
    .map((slot) => ({
      start: slot?.start || slot?.startTime || '',
      end: slot?.end || slot?.endTime || '',
    }))
    .filter((slot) => slot.start || slot.end)
    .sort(compareTimeRanges)
}

function cloneWeeklyHourMap(weeklyHourMap = {}) {
  const nextMap = createEmptyWeeklyHourMap()

  DAYS_OF_WEEK.forEach((day) => {
    nextMap[day.key] = sanitizeSlots(weeklyHourMap?.[day.key] || []).map((slot) => ({ ...slot }))
  })

  return nextMap
}

function mergeSlots(existingSlots = [], incomingSlots = []) {
  const merged = [...existingSlots.map((slot) => ({ ...slot }))]

  incomingSlots.forEach((slot) => {
    if (!slot?.start || !slot?.end) {
      return
    }

    const alreadyPresent = merged.some(
      (existingSlot) => existingSlot.start === slot.start && existingSlot.end === slot.end,
    )

    if (!alreadyPresent) {
      merged.push({ start: slot.start, end: slot.end })
    }
  })

  return sanitizeSlots(merged)
}

function countTotalSlots(weeklyHourMap = {}) {
  return DAYS_OF_WEEK.reduce((total, day) => total + (weeklyHourMap?.[day.key]?.length || 0), 0)
}

function formatTimeLabel(value) {
  if (getMinutesFromTime(value) === null) {
    return 'Time not set'
  }

  return dayjs(`2000-01-01T${value}:00`).format('h:mm A')
}

function extractTimeZoneString(timeZoneValue) {
  if (!timeZoneValue) {
    return null
  }

  if (typeof timeZoneValue === 'string') {
    return timeZoneValue
  }

  return (
    timeZoneValue.timezone ||
    timeZoneValue.timeZone ||
    timeZoneValue.zoneId ||
    timeZoneValue.id ||
    null
  )
}

function buildInitialFormData(chamberRoom) {
  const rosterSchedule = chamberRoom?.rosterSchedule || null
  const weeklyHourMap = cloneWeeklyHourMap(rosterSchedule?.weeklyHourMap)
  const roomEventTypes = getRoomRosterEventTypes(chamberRoom)
  const scheduleEventTypes = sanitizeStringList(rosterSchedule?.eventTypes)
  const initialEventTypes = scheduleEventTypes.length > 0 ? scheduleEventTypes : roomEventTypes
  const roomTargetSpecialities = sanitizeStringList(chamberRoom?.eventSubTypesForThisRoom)

  return {
    timezone:
      extractTimeZoneString(rosterSchedule?.timezone) ||
      extractTimeZoneString(rosterSchedule?.timeZone) ||
      extractTimeZoneString(chamberRoom?.clinicLocation?.timeZone) ||
      DEFAULT_TIMEZONE,
    eventTypes: roomEventTypes.length > 0
      ? initialEventTypes.filter((eventType) => roomEventTypes.includes(eventType))
      : initialEventTypes,
    targetSpecialitySlugs: sanitizeStringList(
      rosterSchedule?.targetSpecialitySlugs || roomTargetSpecialities,
    ),
    weeklyHourMap,
  }
}

function getResponseMessage(response, fallbackMessage) {
  return (
    getMessageMapFromCommonResponse(response) ||
    response?.data?.message ||
    response?.response?.data?.message ||
    fallbackMessage
  )
}

export default function ChamberRoomScheduleEditorDialog({
  open,
  chamberRoom,
  targetSpecialities = [],
  onClose,
  onSaved,
}) {
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'))

  const [formData, setFormData] = useState(() => buildInitialFormData(chamberRoom))
  const [saving, setSaving] = useState(false)
  const [copyDialogState, setCopyDialogState] = useState({
    open: false,
    mode: 'day',
    sourceDayKey: null,
    sourceSlotIndex: null,
    selectedTargetDayKeys: [],
  })

  useEffect(() => {
    if (open) {
      setFormData(buildInitialFormData(chamberRoom))
      setCopyDialogState({
        open: false,
        mode: 'day',
        sourceDayKey: null,
        sourceSlotIndex: null,
        selectedTargetDayKeys: [],
      })
    }
  }, [open, chamberRoom])

  const rosterSchedule = chamberRoom?.rosterSchedule || null
  const hasExistingSchedule = !!rosterSchedule
  const roomTitle = chamberRoom?.title || chamberRoom?.name || 'Chamber room'
  const clinicBrandName =
    chamberRoom?.clinicBrand?.name || chamberRoom?.clinicBrandName || chamberRoom?.clinicBrand?.title || ''
  const clinicLocationName =
    chamberRoom?.clinicLocation?.name ||
    chamberRoom?.clinicLocationName ||
    chamberRoom?.clinicLocation?.title ||
    ''
  const openDaysCount = useMemo(
    () =>
      DAYS_OF_WEEK.filter((day) => (formData?.weeklyHourMap?.[day.key] || []).length > 0).length,
    [formData?.weeklyHourMap],
  )
  const totalTimeWindows = useMemo(
    () => countTotalSlots(formData?.weeklyHourMap),
    [formData?.weeklyHourMap],
  )
  const eventTypeRosteringRows = useMemo(
    () => buildEventTypeRosteringRows(chamberRoom),
    [chamberRoom],
  )

  const timeZoneOptions = useMemo(() => {
    const timezoneMap = new Map()

    timezones_list.forEach((timezoneItem) => {
      const timezoneValue = timezoneItem?.timezone
      if (!timezoneValue || timezoneMap.has(timezoneValue)) {
        return
      }

      timezoneMap.set(timezoneValue, {
        value: timezoneValue,
        label: timezoneItem?.name || timezoneValue,
      })
    })

    if (formData?.timezone && !timezoneMap.has(formData.timezone)) {
      timezoneMap.set(formData.timezone, {
        value: formData.timezone,
        label: formData.timezone,
      })
    }

    return Array.from(timezoneMap.values())
  }, [formData?.timezone])

  const eventTypeOptions = useMemo(() => {
    const optionMap = new Map()
    const rosterEventTypes = getRoomRosterEventTypes(chamberRoom)
    const fallbackEventTypes =
      rosterEventTypes.length > 0
        ? rosterEventTypes
        : EVENT_TYPES_ARRAY.map((eventType) => eventType.id)

    sanitizeStringList(fallbackEventTypes).forEach((eventTypeId) => {
      optionMap.set(eventTypeId, {
        id: eventTypeId,
        label: getEventTypeLabel(eventTypeId),
      })
    })

    return Array.from(optionMap.values())
  }, [chamberRoom, formData?.eventTypes])

  const targetSpecialityOptions = useMemo(() => {
    const optionMap = new Map()

    targetSpecialities.forEach((speciality) => {
      const value = speciality?.slug || speciality?.value || speciality?.id
      if (!value || optionMap.has(value)) {
        return
      }

      optionMap.set(value, {
        value,
        label: speciality?.title || speciality?.label || speciality?.name || value,
      })
    })

    sanitizeStringList([
      ...(chamberRoom?.eventSubTypesForThisRoom || []),
      ...(formData?.targetSpecialitySlugs || []),
    ]).forEach((specialitySlug) => {
      if (optionMap.has(specialitySlug)) {
        return
      }

      optionMap.set(specialitySlug, {
        value: specialitySlug,
        label: toReadableLabel(specialitySlug),
      })
    })

    return Array.from(optionMap.values())
  }, [chamberRoom?.eventSubTypesForThisRoom, formData?.targetSpecialitySlugs, targetSpecialities])

  const copySourceDay = DAYS_OF_WEEK.find((day) => day.key === copyDialogState.sourceDayKey) || null
  const copySourceSlot =
    copyDialogState.mode === 'slot' && copyDialogState.sourceDayKey
      ? formData?.weeklyHourMap?.[copyDialogState.sourceDayKey]?.[copyDialogState.sourceSlotIndex] || null
      : null
  const copyTargetOptions = useMemo(
    () => DAYS_OF_WEEK.filter((day) => day.key !== copyDialogState.sourceDayKey),
    [copyDialogState.sourceDayKey],
  )

  const handleFieldChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const handleOpenDay = (dayKey) => {
    setFormData((prev) => ({
      ...prev,
      weeklyHourMap: {
        ...prev.weeklyHourMap,
        [dayKey]:
          prev.weeklyHourMap?.[dayKey]?.length > 0
            ? sanitizeSlots(prev.weeklyHourMap[dayKey])
            : [{ ...DEFAULT_SLOT }],
      },
    }))
  }

  const handleCloseDay = (dayKey) => {
    setFormData((prev) => ({
      ...prev,
      weeklyHourMap: {
        ...prev.weeklyHourMap,
        [dayKey]: [],
      },
    }))
  }

  const handleAddTimeSlot = (dayKey) => {
    setFormData((prev) => {
      const currentSlots = sanitizeSlots(prev.weeklyHourMap?.[dayKey] || [])
      const lastSlot = currentSlots[currentSlots.length - 1]
      const lastEndMinutes = getMinutesFromTime(lastSlot?.end)
      const nextStart = lastEndMinutes !== null ? lastSlot.end : DEFAULT_SLOT.start
      const nextEnd = lastEndMinutes !== null ? getTimeFromMinutes(lastEndMinutes + 60) : DEFAULT_SLOT.end

      return {
        ...prev,
        weeklyHourMap: {
          ...prev.weeklyHourMap,
          [dayKey]: sanitizeSlots([...currentSlots, { start: nextStart, end: nextEnd }]),
        },
      }
    })
  }

  const handleTimeSlotChange = (dayKey, index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      weeklyHourMap: {
        ...prev.weeklyHourMap,
        [dayKey]: (prev.weeklyHourMap?.[dayKey] || []).map((slot, slotIndex) =>
          slotIndex === index ? { ...slot, [field]: value } : slot,
        ),
      },
    }))
  }

  const handleRemoveTimeSlot = (dayKey, index) => {
    setFormData((prev) => ({
      ...prev,
      weeklyHourMap: {
        ...prev.weeklyHourMap,
        [dayKey]: (prev.weeklyHourMap?.[dayKey] || []).filter((_, slotIndex) => slotIndex !== index),
      },
    }))
  }

  const openCopyDialog = (mode, sourceDayKey, sourceSlotIndex = null) => {
    setCopyDialogState({
      open: true,
      mode,
      sourceDayKey,
      sourceSlotIndex,
      selectedTargetDayKeys: DAYS_OF_WEEK.filter((day) => day.key !== sourceDayKey).map((day) => day.key),
    })
  }

  const closeCopyDialog = () => {
    setCopyDialogState({
      open: false,
      mode: 'day',
      sourceDayKey: null,
      sourceSlotIndex: null,
      selectedTargetDayKeys: [],
    })
  }

  const setCopyTargets = (nextTargets) => {
    setCopyDialogState((prev) => ({
      ...prev,
      selectedTargetDayKeys: sanitizeStringList(nextTargets),
    }))
  }

  const applyCopyTargets = (mode) => {
    if (!copyDialogState.sourceDayKey) {
      return
    }

    const sourceSlots = sanitizeSlots(formData?.weeklyHourMap?.[copyDialogState.sourceDayKey] || [])

    if (mode === 'slot' && !copySourceSlot) {
      toast.error('The time window you selected is no longer available.')
      closeCopyDialog()
      return
    }

    if (copyDialogState.selectedTargetDayKeys.length === 0) {
      toast.error('Select at least one day to continue.')
      return
    }

    setFormData((prev) => {
      const nextWeeklyHourMap = cloneWeeklyHourMap(prev.weeklyHourMap)

      copyDialogState.selectedTargetDayKeys.forEach((targetDayKey) => {
        if (mode === 'day') {
          nextWeeklyHourMap[targetDayKey] = sourceSlots.map((slot) => ({ ...slot }))
          return
        }

        nextWeeklyHourMap[targetDayKey] = mergeSlots(nextWeeklyHourMap[targetDayKey], [copySourceSlot])
      })

      return {
        ...prev,
        weeklyHourMap: nextWeeklyHourMap,
      }
    })

    toast.success(
      mode === 'day'
        ? `Copied ${copySourceDay?.label || 'day'} to ${copyDialogState.selectedTargetDayKeys.length} day(s).`
        : `Cloned ${formatTimeLabel(copySourceSlot?.start)} - ${formatTimeLabel(copySourceSlot?.end)} to ${copyDialogState.selectedTargetDayKeys.length} day(s).`,
    )
    closeCopyDialog()
  }

  const validateWeeklyHourMap = (weeklyHourMap) => {
    for (const day of DAYS_OF_WEEK) {
      const slots = sanitizeSlots(weeklyHourMap?.[day.key] || [])

      for (let slotIndex = 0; slotIndex < slots.length; slotIndex += 1) {
        const slot = slots[slotIndex]
        const startMinutes = getMinutesFromTime(slot.start)
        const endMinutes = getMinutesFromTime(slot.end)

        if (startMinutes === null || endMinutes === null) {
          return {
            valid: false,
            message: `Please choose valid start and end times for ${day.label}.`,
          }
        }

        if (endMinutes <= startMinutes) {
          return {
            valid: false,
            message: `${day.label} window ${slotIndex + 1} must end after it starts.`,
          }
        }

        if (slotIndex > 0) {
          const previousSlot = slots[slotIndex - 1]
          const previousEndMinutes = getMinutesFromTime(previousSlot.end)

          if (previousEndMinutes !== null && startMinutes < previousEndMinutes) {
            return {
              valid: false,
              message: `${day.label} has overlapping time windows. Please fix them before saving.`,
            }
          }
        }
      }
    }

    return { valid: true }
  }

  const handleSave = async () => {
    if (!chamberRoom?.id) {
      toast.error('Chamber room details are missing. Please close this dialog and try again.')
      return
    }

    const validation = validateWeeklyHourMap(formData.weeklyHourMap)
    if (!validation.valid) {
      toast.error(validation.message)
      return
    }

    const payload = {
      chamberRoomId: chamberRoom.id,
      timezone: formData.timezone || DEFAULT_TIMEZONE,
      eventTypes: sanitizeStringList(formData.eventTypes),
      targetSpecialitySlugs: sanitizeStringList(formData.targetSpecialitySlugs),
      weeklyHourMap: cloneWeeklyHourMap(formData.weeklyHourMap),
    }

    setSaving(true)

    const response = hasExistingSchedule
      ? await updateRosterSchedule(chamberRoom.id, payload)
      : await createRosterSchedule(payload)

    setSaving(false)

    if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
      const responseData = getDataMapFromCommonResponse(response)
      const savedSchedule =
        responseData?.['roster-schedule'] || responseData?.rosterSchedule || responseData?.schedule || payload

      toast.success(hasExistingSchedule ? 'Room schedule updated.' : 'Room schedule created.')
      onSaved?.({
        chamberRoomId: chamberRoom.id,
        rosterSchedule: savedSchedule,
        payload,
      })
      onClose?.()
      return
    }

    toast.error(
      getResponseMessage(
        response,
        hasExistingSchedule ? 'Unable to update room schedule right now.' : 'Unable to create room schedule right now.',
      ),
    )
  }

  if (!chamberRoom) {
    return null
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={saving ? undefined : onClose}
        fullScreen={fullScreen}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1.5 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
            <Box sx={{ minWidth: 0 }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.75 }}>
                <EditCalendar color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {hasExistingSchedule ? 'Edit chamber room schedule' : 'Set chamber room schedule'}
                </Typography>
              </Stack>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {roomTitle}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {[clinicBrandName, clinicLocationName].filter(Boolean).join(' • ') || 'Support portal roster management'}
              </Typography>
            </Box>
            <IconButton onClick={onClose} disabled={saving} size="small">
              <Close />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent dividers sx={{ px: { xs: 2, md: 3 }, py: 2.5 }}>
          <Alert severity="info" sx={{ mb: 2.5, borderRadius: 2 }}>
            These weekly hours drive chamber-room availability on the overall roster. Use “Copy day” to mirror an entire day, or “Clone” on a single time window to reuse just one slot on other days.
          </Alert>

          <Grid container spacing={2} sx={{ mb: 2.5 }}>
            <Grid item xs={12} md={4}>
              <Card
                variant="outlined"
                sx={{
                  borderRadius: 3,
                  height: '100%',
                  backgroundColor: alpha(theme.palette.primary.main, 0.04),
                }}
              >
                <CardContent>
                  <Typography variant="overline" color="text.secondary">
                    Room
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                    {roomTitle}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {clinicBrandName || 'Clinic brand not available'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {clinicLocationName || 'Clinic location not available'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card
                variant="outlined"
                sx={{
                  borderRadius: 3,
                  height: '100%',
                  backgroundColor: alpha(theme.palette.success.main, 0.05),
                }}
              >
                <CardContent>
                  <Typography variant="overline" color="text.secondary">
                    Weekly coverage
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: 1, mb: 1.25, flexWrap: 'wrap' }} useFlexGap>
                    <Chip
                      color={openDaysCount > 0 ? 'success' : 'default'}
                      label={`${openDaysCount} open day${openDaysCount === 1 ? '' : 's'}`}
                    />
                    <Chip
                      color={totalTimeWindows > 0 ? 'primary' : 'default'}
                      label={`${totalTimeWindows} time window${totalTimeWindows === 1 ? '' : 's'}`}
                    />
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    {openDaysCount > 0
                      ? 'You can fine-tune each day below and reuse the same hours wherever needed.'
                      : 'No active hours yet. Open a day below to begin, or copy a day across the week.'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card
                variant="outlined"
                sx={{
                  borderRadius: 3,
                  height: '100%',
                  backgroundColor: alpha(theme.palette.warning.main, 0.06),
                }}
              >
                <CardContent>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    <TipsAndUpdates color="warning" />
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      Helpful shortcuts
                    </Typography>
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
                    Copy full day schedules when the room follows a repeated pattern.
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
                    Clone a single time window when you only want to reuse one slot.
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Leaving event types or target specialities empty keeps the room’s default permissions.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Card variant="outlined" sx={{ borderRadius: 3, mb: 2.5 }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                Schedule settings
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Autocomplete
                    options={timeZoneOptions}
                    value={timeZoneOptions.find((option) => option.value === formData.timezone) || null}
                    onChange={(_, option) => handleFieldChange('timezone', option?.value || DEFAULT_TIMEZONE)}
                    getOptionLabel={(option) => option?.label || option?.value || ''}
                    isOptionEqualToValue={(option, value) => option?.value === value?.value}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Timezone"
                        helperText="Room slots are generated using this timezone."
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <Autocomplete
                    multiple
                    options={eventTypeOptions}
                    value={eventTypeOptions.filter((option) => formData.eventTypes.includes(option.id))}
                    onChange={(_, nextOptions) =>
                      handleFieldChange(
                        'eventTypes',
                        nextOptions.map((option) => option.id),
                      )
                    }
                    getOptionLabel={(option) => option?.label || option?.id || ''}
                    isOptionEqualToValue={(option, value) => option?.id === value?.id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Allowed event types"
                        helperText="If you leave this empty, the room’s default event types stay in effect."
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <Autocomplete
                    multiple
                    options={targetSpecialityOptions}
                    value={targetSpecialityOptions.filter((option) =>
                      formData.targetSpecialitySlugs.includes(option.value),
                    )}
                    onChange={(_, nextOptions) =>
                      handleFieldChange(
                        'targetSpecialitySlugs',
                        nextOptions.map((option) => option.value),
                      )
                    }
                    getOptionLabel={(option) => option?.label || option?.value || ''}
                    isOptionEqualToValue={(option, value) => option?.value === value?.value}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Target specialities"
                        helperText="Leave empty to keep the chamber room’s default speciality scope."
                      />
                    )}
                  />
                </Grid>
              </Grid>
              {eventTypeRosteringRows.length > 0 && (
                <Box
                  sx={{
                    mt: 2,
                    pt: 2,
                    borderTop: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography variant="overline" color="text.secondary">
                    Event rostering rules
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }} useFlexGap>
                    {eventTypeRosteringRows.map((detail) => {
                      const isOnCall = detail.rosteringMode === ROSTERING_MODE_ON_CALL
                      return (
                        <Chip
                          key={detail.eventType}
                          icon={
                            detail.rostered
                              ? isOnCall ? <PhoneInTalk /> : <VideoCameraFront />
                              : <EventBusy />
                          }
                          label={`${getEventTypeLabel(detail.eventType)}: ${
                            detail.rostered ? getRosteringModeLabel(detail.rosteringMode) : 'Not rostered'
                          }`}
                          color={detail.rostered ? isOnCall ? 'warning' : 'info' : 'default'}
                          variant={detail.rostered ? 'filled' : 'outlined'}
                        />
                      )
                    })}
                  </Stack>
                </Box>
              )}
            </CardContent>
          </Card>

          <Box sx={{ mb: 1.5 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
              Weekly hours
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Add one or more time windows per day. Closed days simply stay empty.
            </Typography>
          </Box>

          <Grid container spacing={2}>
            {DAYS_OF_WEEK.map((day) => {
              const daySlots = formData?.weeklyHourMap?.[day.key] || []
              const isOpen = daySlots.length > 0

              return (
                <Grid item xs={12} md={6} key={day.key}>
                  <Card
                    variant="outlined"
                    sx={{
                      borderRadius: 3,
                      borderColor: isOpen ? alpha(theme.palette.success.main, 0.3) : 'divider',
                      backgroundColor: isOpen
                        ? alpha(theme.palette.success.main, 0.04)
                        : alpha(theme.palette.text.secondary, 0.03),
                      height: '100%',
                    }}
                  >
                    <CardContent>
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={1.5}
                        justifyContent="space-between"
                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                        sx={{ mb: 2 }}
                      >
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            {day.label}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {isOpen
                              ? `${daySlots.length} time window${daySlots.length === 1 ? '' : 's'}`
                              : 'Closed'}
                          </Typography>
                        </Box>
                        <Chip
                          label={isOpen ? 'Open' : 'Closed'}
                          color={isOpen ? 'success' : 'default'}
                          variant={isOpen ? 'filled' : 'outlined'}
                        />
                      </Stack>

                      <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }} useFlexGap>
                        {isOpen ? (
                          <>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<Add />}
                              onClick={() => handleAddTimeSlot(day.key)}
                            >
                              Add window
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<ContentCopy />}
                              onClick={() => openCopyDialog('day', day.key)}
                            >
                              Copy day
                            </Button>
                            <Button
                              size="small"
                              color="inherit"
                              onClick={() => handleCloseDay(day.key)}
                            >
                              Mark closed
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button
                              size="small"
                              variant="contained"
                              startIcon={<Add />}
                              onClick={() => handleOpenDay(day.key)}
                            >
                              Open day
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<ContentCopy />}
                              onClick={() => openCopyDialog('day', day.key)}
                            >
                              Copy day
                            </Button>
                          </>
                        )}
                      </Stack>

                      {isOpen ? (
                        <Stack spacing={1.25}>
                          {daySlots.map((slot, index) => (
                            <Box
                              key={`${day.key}-${index}`}
                              sx={{
                                p: 1.5,
                                borderRadius: 2,
                                border: '1px solid',
                                borderColor: 'divider',
                                backgroundColor: 'background.paper',
                              }}
                            >
                              <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                spacing={1}
                                alignItems={{ xs: 'stretch', sm: 'center' }}
                              >
                                <TextField
                                  size="small"
                                  type="time"
                                  label="From"
                                  value={slot.start || ''}
                                  onChange={(event) =>
                                    handleTimeSlotChange(day.key, index, 'start', event.target.value)
                                  }
                                  InputLabelProps={{ shrink: true }}
                                  sx={{ flex: 1 }}
                                />
                                <TextField
                                  size="small"
                                  type="time"
                                  label="To"
                                  value={slot.end || ''}
                                  onChange={(event) =>
                                    handleTimeSlotChange(day.key, index, 'end', event.target.value)
                                  }
                                  InputLabelProps={{ shrink: true }}
                                  sx={{ flex: 1 }}
                                />
                                <Stack direction="row" spacing={0.5} alignItems="center">
                                  <Tooltip title="Clone this time window to other days">
                                    <IconButton
                                      color="primary"
                                      onClick={() => openCopyDialog('slot', day.key, index)}
                                    >
                                      <ContentCopy fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Remove this time window">
                                    <IconButton
                                      color="error"
                                      onClick={() => handleRemoveTimeSlot(day.key, index)}
                                    >
                                      <DeleteOutline fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Stack>
                              </Stack>
                              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                {slot.start && slot.end
                                  ? `${formatTimeLabel(slot.start)} to ${formatTimeLabel(slot.end)}`
                                  : 'Pick both times for this window.'}
                              </Typography>
                            </Box>
                          ))}
                        </Stack>
                      ) : (
                        <Box
                          sx={{
                            p: 2,
                            borderRadius: 2,
                            border: '1px dashed',
                            borderColor: 'divider',
                            textAlign: 'center',
                          }}
                        >
                          <EventBusy sx={{ color: 'text.secondary', opacity: 0.6, mb: 1 }} />
                          <Typography variant="body2" color="text.secondary">
                            This room stays closed on {day.label} until you add hours or copy them from another day.
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              )
            })}
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: { xs: 2, md: 3 }, py: 2 }}>
          <Button onClick={onClose} disabled={saving} color="inherit">
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <AccessTime />}
          >
            {saving ? 'Saving...' : hasExistingSchedule ? 'Save schedule' : 'Create schedule'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={copyDialogState.open}
        onClose={closeCopyDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {copyDialogState.mode === 'day'
            ? `Copy ${copySourceDay?.label || 'day'} schedule`
            : `Clone ${copySourceDay?.label || 'day'} time window`}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {copyDialogState.mode === 'day'
              ? 'Selected days will match this day exactly, including closed days.'
              : `This will add ${formatTimeLabel(copySourceSlot?.start)} to ${formatTimeLabel(copySourceSlot?.end)} to the selected days without duplicating the same window.`}
          </Typography>

          <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }} useFlexGap>
            <Button
              size="small"
              variant="outlined"
              onClick={() =>
                setCopyTargets(
                  copyTargetOptions
                    .filter((day) => WEEKDAY_KEYS.includes(day.key))
                    .map((day) => day.key),
                )
              }
            >
              Weekdays
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() =>
                setCopyTargets(
                  copyTargetOptions
                    .filter((day) => WEEKEND_KEYS.includes(day.key))
                    .map((day) => day.key),
                )
              }
            >
              Weekend
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setCopyTargets(copyTargetOptions.map((day) => day.key))}
            >
              All
            </Button>
            <Button size="small" color="inherit" onClick={() => setCopyTargets([])}>
              Clear
            </Button>
          </Stack>

          <Stack spacing={1}>
            {copyTargetOptions.map((day) => {
              const checked = copyDialogState.selectedTargetDayKeys.includes(day.key)

              return (
                <Box
                  key={day.key}
                  sx={{
                    border: '1px solid',
                    borderColor: checked ? alpha(theme.palette.primary.main, 0.35) : 'divider',
                    borderRadius: 2,
                    px: 1.5,
                    py: 0.5,
                  }}
                >
                  <Button
                    fullWidth
                    color="inherit"
                    onClick={() =>
                      setCopyTargets(
                        checked
                          ? copyDialogState.selectedTargetDayKeys.filter((selectedKey) => selectedKey !== day.key)
                          : [...copyDialogState.selectedTargetDayKeys, day.key],
                      )
                    }
                    sx={{
                      justifyContent: 'space-between',
                      textTransform: 'none',
                      px: 0,
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {day.label}
                    </Typography>
                    <Checkbox checked={checked} />
                  </Button>
                </Box>
              )
            })}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCopyDialog} color="inherit">
            Cancel
          </Button>
          <Button variant="contained" onClick={() => applyCopyTargets(copyDialogState.mode)}>
            {copyDialogState.mode === 'day' ? 'Copy schedule' : 'Clone time window'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
